import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  InputText,
  SectionCard,
  Select,
  type SelectOption,
} from "@/deps/ui-library";
import {
  useCloneTemplate,
  useFiscalYears,
  useReportFrameworks,
  useReportTemplate,
  useSaveTemplate,
  useTemplatePreview,
} from "@/api/queries";
import type { ReportTemplateDetail, ReportTag, TemplateLine } from "@/api/types";
import { useCan } from "@/app/session";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/cn";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { addable, flip, move, newLine, remove, tagsElsewhere } from "./templateLines";
import { offered, tagLabel } from "../tagLabel";

const KIND_LABEL: Record<TemplateLine["kind"], string> = {
  head: "Heading",
  tag: "Tag line",
  result: "Year's result",
  sum: "Subtotal",
  grand: "Total",
};

const first = (value: SelectOption | SelectOption[] | null) =>
  String((Array.isArray(value) ? value[0] : value)?.value ?? "");

/** Waits for typing to stop, so a preview isn't asked for on every key. */
function useSettled<T>(value: T, ms = 350): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

function LineDetail({
  lines,
  index,
  tags,
  onChange,
  onMove,
  onRemove,
}: {
  lines: TemplateLine[];
  index: number;
  tags: ReportTag[];
  onChange: (line: TemplateLine) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const line = lines[index]!;
  const taken = tagsElsewhere(lines, line.code);
  const free = offered(tags).filter((tag) => !taken.has(tag.code) && !line.tags.includes(tag.code));
  const terms = addable(lines, index).filter(
    (candidate) => !line.formula.some((term) => term.slice(1) === candidate.code),
  );
  const labelOf = (code: string) => lines.find((other) => other.code === code)?.label ?? code;

  return (
    <div className="border-line-soft flex flex-col gap-3 border-t p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge colorScheme="default" size="sm">
          {KIND_LABEL[line.kind]}
        </Badge>
        <span className="flex-1" />
        <Button variant="secondary" size="xs" disabled={index === 0} onClick={() => onMove(-1)}>
          Move up
        </Button>
        <Button
          variant="secondary"
          size="xs"
          disabled={index === lines.length - 1}
          onClick={() => onMove(1)}
        >
          Move down
        </Button>
        <Button variant="secondary" size="xs" disabled={line.tie !== null} onClick={onRemove}>
          Delete line
        </Button>
      </div>

      <InputText
        label="Label"
        value={line.label}
        onChange={(event) => onChange({ ...line, label: event.target.value })}
      />

      {line.kind === "tag" ? (
        <>
          <div className="flex flex-wrap gap-1">
            {line.tags.map((code) => (
              <Button
                key={code}
                variant="secondary"
                size="xs"
                aria-label={`Remove ${code}`}
                onClick={() => onChange({ ...line, tags: line.tags.filter((tag) => tag !== code) })}
              >
                {`${code} ×`}
              </Button>
            ))}
          </div>
          <Select
            label="Add a tag"
            options={free.map((tag) => ({ value: tag.code, label: tagLabel(tag, tags) }))}
            value=""
            showClearButton={false}
            onChange={(details) => {
              const code = first(details.value);
              if (code) onChange({ ...line, tags: [...line.tags, code] });
            }}
          />
          <Checkbox
            label="Show credit balances as positive"
            checked={line.negate}
            onCheckedChange={(details: { checked: boolean | "indeterminate" }) =>
              onChange({ ...line, negate: details.checked === true })
            }
          />
        </>
      ) : null}

      {line.kind === "sum" || line.kind === "grand" ? (
        <>
          <div className="flex flex-wrap gap-1">
            {line.formula.map((term) => (
              <span key={term} className="flex items-center gap-1">
                <Button
                  variant="secondary"
                  size="xs"
                  aria-label={`Switch sign of ${labelOf(term.slice(1))}`}
                  onClick={() =>
                    onChange({
                      ...line,
                      formula: line.formula.map((other) => (other === term ? flip(term) : other)),
                    })
                  }
                >
                  {`${term[0] === "-" ? "−" : "+"} ${labelOf(term.slice(1))}`}
                </Button>
                <Button
                  variant="secondary"
                  size="xs"
                  aria-label={`Remove ${labelOf(term.slice(1))}`}
                  onClick={() =>
                    onChange({ ...line, formula: line.formula.filter((other) => other !== term) })
                  }
                >
                  ×
                </Button>
              </span>
            ))}
          </div>
          <Select
            label="Add a line to add up"
            options={terms.map((candidate) => ({ value: candidate.code, label: candidate.label }))}
            value=""
            showClearButton={false}
            onChange={(details) => {
              const code = first(details.value);
              if (code) onChange({ ...line, formula: [...line.formula, `+${code}`] });
            }}
          />
          <Checkbox
            label="Emphasise as a headline subtotal"
            checked={line.strong}
            onCheckedChange={(details: { checked: boolean | "indeterminate" }) =>
              onChange({ ...line, strong: details.checked === true })
            }
          />
        </>
      ) : null}

      {line.tie ? (
        <p className="text-fg-muted text-xs">
          This line proves the statement against the trial balance, so it stays.
        </p>
      ) : null}
    </div>
  );
}

function Preview({ template, lines }: { template: ReportTemplateDetail; lines: TemplateLine[] }) {
  const settled = useSettled(lines);
  const to = today();
  const { data: years } = useFiscalYears();
  const year = years?.find((candidate) => candidate.start <= to && to <= candidate.end);
  const { data, error } = useTemplatePreview(
    {
      frameworkCode: template.summary.frameworkCode,
      statement: template.summary.statement,
      lines: settled,
      from: template.summary.statement === "pl" ? (year?.start ?? null) : null,
      to,
    },
    year !== undefined,
  );

  return (
    <SectionCard
      title="Live preview"
      note={`Your figures · ${year?.label ?? ""} · to ${formatDate(to)}`}
      flush
    >
      {error ? (
        <div className="p-3">
          <Alert
            role="alert"
            config={{ variant: "subtle", colorScheme: "warning" }}
            description={(error as Error).message}
          />
        </div>
      ) : null}
      {data ? (
        <>
          <ul className="flex flex-col text-sm" aria-label="Preview">
            {data.lines.map((line) => (
              <li
                key={line.code}
                className={cn(
                  "border-line-soft flex justify-between gap-3 border-b px-4 py-1.5",
                  line.kind === "grand" && "bg-muted font-semibold",
                  (line.kind === "sum" || line.strong) && "font-medium",
                  line.kind === "head" && "text-muted-foreground font-medium",
                  line.kind === "tag" && "pl-8",
                )}
              >
                <span>{line.label}</span>
                <span className="num">{line.amount === null ? "" : formatAmount(line.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-2 p-3">
            {data.unmapped.length > 0 ? (
              <Alert
                config={{ variant: "subtle", colorScheme: "warning" }}
                description={`${data.unmapped.length} ${data.unmapped.length === 1 ? "account is" : "accounts are"} on no line: ${data.unmapped.map((o) => `${o.code} ${o.name}`).join(", ")}.`}
              />
            ) : null}
            {Number(data.checks.difference) !== 0 ? (
              <Alert
                role="alert"
                config={{ variant: "subtle", colorScheme: "danger" }}
                description={`The totals differ from the trial balance by ${formatAmount(data.checks.difference)}. A subtotal may be skipping a line.`}
              />
            ) : (
              <p className="text-fg-muted text-xs">Ties to the trial balance.</p>
            )}
          </div>
        </>
      ) : null}
    </SectionCard>
  );
}

function Editor({ template }: { template: ReportTemplateDetail }) {
  const can = useCan();
  const navigate = useNavigate();
  const editable = !template.summary.supplied && can("reports.layout.edit");
  const { data: frameworks } = useReportFrameworks();
  const save = useSaveTemplate();
  const clone = useCloneTemplate();
  const [lines, setLines] = useState(template.lines);
  const [name, setName] = useState(template.summary.name);
  const [selected, setSelected] = useState<string | null>(null);

  // A save or a different template brings a new version.
  useEffect(() => {
    setLines(template.lines);
    setName(template.summary.name);
  }, [template]);

  const groups =
    template.summary.statement === "pl"
      ? ["Income", "Expenses"]
      : ["Assets", "Liabilities", "Equity"];
  const tags = (
    frameworks?.find((f) => f.code === template.summary.frameworkCode)?.tags ?? []
  ).filter((tag) => groups.includes(tag.group));

  const dirty =
    name !== template.summary.name || JSON.stringify(lines) !== JSON.stringify(template.lines);
  const index = lines.findIndex((line) => line.code === selected);
  const add = (kind: "tag" | "sum" | "head") => {
    const line = newLine(kind, lines);
    const at = index >= 0 ? index + 1 : lines.length;
    setLines([...lines.slice(0, at), line, ...lines.slice(at)]);
    setSelected(line.code);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={template.summary.name}
        subtitle={
          template.summary.supplied
            ? `${template.summary.version} · read-only. Clone it to change the lines.`
            : `Company format · ${template.summary.version}`
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/reports/templates" className="text-primary text-xs underline">
              Back to templates
            </Link>
            {can("reports.layout.edit") ? (
              <Button
                variant="secondary"
                size="sm"
                leftIcon="Copy"
                disabled={clone.isPending}
                onClick={() =>
                  clone.mutate(template.summary.code, {
                    onSuccess: (made) =>
                      void navigate({
                        to: "/reports/templates/$code",
                        params: { code: made.summary.code },
                      }),
                  })
                }
              >
                Clone
              </Button>
            ) : null}
            {editable ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!dirty}
                  onClick={() => {
                    setLines(template.lines);
                    setName(template.summary.name);
                  }}
                >
                  Revert
                </Button>
                <Button
                  colorScheme="primary"
                  variant="solid"
                  size="sm"
                  disabled={!dirty || save.isPending}
                  onClick={() =>
                    save.mutate({ id: template.summary.code, name: name.trim(), lines })
                  }
                >
                  {save.isPending ? "Saving" : "Save template"}
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      {(save.error ?? clone.error) ? (
        <Alert
          role="alert"
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={((save.error ?? clone.error) as Error).message}
        />
      ) : null}

      {editable ? (
        <div className="w-96">
          <InputText label="Name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card size="none" className="overflow-hidden">
          {editable ? (
            <div className="border-line-soft flex flex-wrap gap-2 border-b p-3">
              <Button variant="secondary" size="xs" onClick={() => add("tag")}>
                Add tag line
              </Button>
              <Button variant="secondary" size="xs" onClick={() => add("sum")}>
                Add subtotal
              </Button>
              <Button variant="secondary" size="xs" onClick={() => add("head")}>
                Add heading
              </Button>
            </div>
          ) : null}
          <ul className="flex flex-col" aria-label="Lines">
            {lines.map((line, position) => (
              <li key={line.code} className="border-line-soft border-b last:border-0">
                <Button
                  variant="ghost"
                  colorScheme="default"
                  size="sm"
                  className={cn(
                    "h-auto w-full justify-between gap-3 rounded-none px-3 py-2 text-left font-normal",
                    selected === line.code && "bg-muted",
                    line.kind === "head" && "text-muted-foreground font-medium",
                    (line.kind === "sum" || line.kind === "grand" || line.strong) && "font-medium",
                  )}
                  disabled={!editable}
                  aria-pressed={selected === line.code}
                  onClick={() => setSelected(selected === line.code ? null : line.code)}
                >
                  <span className="flex w-full items-center justify-between gap-3">
                    <span>{line.label}</span>
                    <span className="text-fg-muted text-xs">{KIND_LABEL[line.kind]}</span>
                  </span>
                </Button>
                {editable && selected === line.code ? (
                  <LineDetail
                    lines={lines}
                    index={position}
                    tags={tags}
                    onChange={(changed) =>
                      setLines(lines.map((other) => (other.code === line.code ? changed : other)))
                    }
                    onMove={(by) => setLines(move(lines, position, by))}
                    onRemove={() => {
                      setLines(remove(lines, line.code));
                      setSelected(null);
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
        <Preview template={template} lines={lines} />
      </div>
    </div>
  );
}

/** One format's lines, with what they'd read beside them (ADR-0061). */
export function TemplateEditorPage() {
  const { code } = useParams({ from: "/reports/templates/$code" });
  const { data, isPending, error } = useReportTemplate(code);

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading</p>;
  if (error || !data) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        description={error ? (error as Error).message : "That template could not be loaded."}
      />
    );
  }
  return <Editor template={data} />;
}
